import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  Download,
  FileText,
  Loader2,
  Plus,
  Settings2,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router";
import {
  analysisDocumentsApi,
  type AnalysisDocument,
} from "@/api/analysis-api";
import type { AnalysisTemplate, BuiltinAnalysisTemplate } from "@/api/deals-api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getErrorMessage } from "@/lib/api-error";
import type { DealTransformed } from "@/lib/schema/deal-schema";
import { cn } from "@/lib/utils";
import { AnalysisDocumentEditor } from "@/components/deal-detail/analysis/analysis-document-editor";
import {
  exportAnalysisToPdf,
  exportAnalysisToWord,
} from "@/components/deal-detail/analysis/analysis-export";
import { documentPlainText } from "@/components/deal-detail/analysis/document-html";
import { analysisIcon } from "@/components/deal-detail/analysis/analysis-icons";
import { useWorkspaceStore } from "@/store/workspace-store";

/**
 * The Analysis tab: pick an analysis the fund has defined, generate it, edit it.
 *
 * Distinct from the DGPT reports, which are ours and fixed. Everything here was
 * written by the fund in Settings, which is why an empty state points there
 * rather than offering a generate button that would produce nothing.
 */

/** How often to look for finished sections while the worker is running. */
const POLL_INTERVAL_MS = 3000;
/** Stop polling eventually — a crashed worker should not spin forever. */
const MAX_POLL_MS = 4 * 60 * 1000;

interface AnalysisDocumentsTabProps {
  currentDeal: DealTransformed;
  /** The tab is mounted from first load; fetching waits until it is looked at. */
  active: boolean;
  /**
   * The open document as plain text, for the chat panel. Lifted to the page
   * because the chat lives in a sibling panel, not inside this tab.
   */
  onDocumentContextChange?: (context: string | null) => void;
  /**
   * Open this agent's report, sent when it is chosen from the Reports tab.
   * Carries a timestamp so choosing the same report twice still re-selects it
   * after the user has picked something else here in between.
   */
  openRequest?: { templateId: string; at: number } | null;
}

export default function AnalysisDocumentsTab({
  currentDeal,
  active,
  onDocumentContextChange,
  openRequest,
}: AnalysisDocumentsTabProps) {
  const dealId = currentDeal.applicationId;
  const { activeWorkspaceId } = useWorkspaceStore();
  // Settings routes are workspace-scoped, so the link needs the current one.
  const settingsPath = `/${activeWorkspaceId}/settings/analysis`;
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ["analysis-documents", dealId], [dealId]);

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [everActive, setEverActive] = useState(active);
  const [pollingSince, setPollingSince] = useState<number | null>(null);

  useEffect(() => {
    if (active) setEverActive(true);
  }, [active]);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey,
    queryFn: () => analysisDocumentsApi.list(dealId),
    enabled: Boolean(dealId) && everActive,
    refetchInterval: pollingSince ? POLL_INTERVAL_MS : false,
  });

  const documents = data?.documents ?? [];
  const templates = data?.templates ?? [];
  const builtins = data?.builtins ?? [];

  /**
   * One row per analysis the fund can run, with its document if it has one.
   * Built-ins are only offered once nothing else exists — otherwise a fund that
   * has written its own templates sees ours competing with them.
   */
  const choices = useMemo(() => {
    const rows = templates.map((template) => ({
      key: String(template._id),
      name: template.name,
      icon: template.icon,
      sectionCount: template.sections.length,
      document: documents.find(
        (candidate) => candidate.template_id === String(template._id)
      ),
      builtin: false as const,
    }));

    const generatedBuiltins = documents
      .filter((document) => document.template_id.startsWith("builtin-"))
      .map((document) => ({
        key: document.template_id,
        name: document.template_name,
        icon: document.template_icon,
        sectionCount: document.sections.length,
        document,
        builtin: true as const,
      }));

    const offeredBuiltins =
      templates.length === 0
        ? builtins
            .filter(
              (builtin) =>
                !generatedBuiltins.some((row) => row.key === builtin.id)
            )
            .map((builtin) => ({
              key: builtin.id,
              name: builtin.name,
              icon: builtin.icon,
              sectionCount: builtin.sections.length,
              document: undefined,
              builtin: true as const,
            }))
        : [];

    return [...rows, ...generatedBuiltins, ...offeredBuiltins];
  }, [builtins, documents, templates]);

  // Land on something sensible: a generated document first, otherwise the first
  // analysis available to run.
  useEffect(() => {
    if (selectedKey && choices.some((choice) => choice.key === selectedKey)) {
      return;
    }
    const withDocument = choices.find((choice) => choice.document);
    setSelectedKey(withDocument?.key ?? choices[0]?.key ?? null);
  }, [choices, selectedKey]);

  // Applied once per request, as soon as the requested agent is among the
  // choices — `choices` changes on every poll, and re-applying then would undo
  // whatever the user picked here afterwards.
  const handledRequestAt = useRef<number | null>(null);
  useEffect(() => {
    if (!openRequest || handledRequestAt.current === openRequest.at) return;
    if (choices.some((choice) => choice.key === openRequest.templateId)) {
      handledRequestAt.current = openRequest.at;
      setSelectedKey(openRequest.templateId);
    }
  }, [openRequest, choices]);

  const selected = choices.find((choice) => choice.key === selectedKey) ?? null;
  const openDocument = selected?.document ?? null;

  const anyRunning = documents.some((document) =>
    document.sections.some((section) => section.status === "running")
  );

  // Poll only while something is actually being written, and give up rather than
  // hammering the API forever if a worker dies without marking the section.
  useEffect(() => {
    if (anyRunning && !pollingSince) {
      setPollingSince(Date.now());
      return;
    }
    if (!anyRunning && pollingSince) {
      setPollingSince(null);
      return;
    }
    if (pollingSince && Date.now() - pollingSince > MAX_POLL_MS) {
      setPollingSince(null);
      toast.error("Generation is taking longer than expected", {
        description: "Reload to check again, or regenerate the section.",
      });
    }
  }, [anyRunning, pollingSince, data]);

  useEffect(() => {
    onDocumentContextChange?.(
      openDocument && active ? documentPlainText(openDocument) : null
    );
  }, [active, onDocumentContextChange, openDocument]);

  const generate = useMutation({
    mutationFn: (params: { sectionId?: string; force?: boolean }) =>
      analysisDocumentsApi.generate({
        dealId,
        templateId: String(selectedKey),
        sectionId: params.sectionId,
        force: params.force,
      }),
    onSuccess: async (result) => {
      if (result.skipped_edited && result.queued === 0) {
        toast("Nothing regenerated", {
          description:
            "Every section has been edited by hand. Use 'Overwrite my edits' to replace them.",
          action: {
            label: "Overwrite my edits",
            onClick: () => generate.mutate({ force: true }),
          },
        });
      } else if (result.queued > 0) {
        toast.success(
          `Writing ${result.queued} ${result.queued === 1 ? "section" : "sections"}`
        );
      }
      setSelectedKey(result.document.template_id);
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: (mutationError) =>
      toast.error(getErrorMessage(mutationError, "Couldn't start the agent")),
  });

  const saveSection = useMutation({
    mutationFn: (params: { sectionId: string; html: string }) =>
      analysisDocumentsApi.saveSection({
        documentId: String(openDocument?._id),
        sectionId: params.sectionId,
        html: params.html,
      }),
    onError: (mutationError) =>
      toast.error(getErrorMessage(mutationError, "Couldn't save your changes")),
  });

  /**
   * Ask for a revision of a selected passage.
   *
   * Not a mutation in the react-query sense: it changes nothing on the server.
   * The author accepts the proposal, the editor applies it to the selection, and
   * the ordinary autosave persists it.
   */
  const reviseSelection = async (params: {
    sectionId: string;
    instruction: string;
    passage: string;
  }) => {
    if (!openDocument) throw new Error("No analysis is open");

    return analysisDocumentsApi.reviseSelection({
      documentId: openDocument._id,
      sectionId: params.sectionId,
      instruction: params.instruction,
      passage: params.passage,
      // The rest of the memo, so a revision reads consistently with what is
      // around it. Context only — the server never edits it.
      documentContext: documentPlainText(openDocument),
    });
  };

  const remove = useMutation({
    mutationFn: (documentId: string) => analysisDocumentsApi.remove(documentId),
    onSuccess: async () => {
      toast.success("Report deleted");
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: (mutationError) =>
      toast.error(getErrorMessage(mutationError, "Couldn't delete the report")),
  });

  if (!everActive) return null;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading agents
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        <p className="text-sm text-destructive">
          {getErrorMessage(error, "Couldn't load agents")}
        </p>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  if (!choices.length) {
    return <NoTemplates settingsPath={settingsPath} />;
  }

  return (
    <div className="flex flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-border bg-card px-4 py-2">
        <AnalysisPicker
          choices={choices}
          selectedKey={selectedKey}
          onSelect={setSelectedKey}
          settingsPath={settingsPath}
        />

        <div className="flex-1" />

        {selected && !openDocument && (
          <Button
            size="sm"
            className="h-8 gap-1.5 text-xs"
            disabled={generate.isPending}
            onClick={() => generate.mutate({})}
          >
            {generate.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            Generate
          </Button>
        )}

        {openDocument && (
          <>
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              disabled={generate.isPending || anyRunning}
              onClick={() => generate.mutate({})}
              title="Regenerate every section that has not been edited by hand"
            >
              {generate.isPending || anyRunning ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )}
              Regenerate
            </Button>

            <ExportMenu
              document={openDocument}
              companyName={currentDeal.overview.name}
            />

            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-destructive"
              disabled={remove.isPending || anyRunning}
              onClick={() => {
                if (
                  window.confirm(
                    `Delete this ${openDocument.template_name} report? The agent itself is not affected.`
                  )
                ) {
                  remove.mutate(openDocument._id);
                }
              }}
              title="Delete this generated report"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
      </div>

      {openDocument ? (
        <AnalysisDocumentEditor
          key={openDocument._id}
          document={openDocument}
          busy={openDocument.sections.some(
            (section) => section.status === "running"
          )}
          onRegenerateSection={(sectionId) => generate.mutate({ sectionId })}
          onSaveSection={async (sectionId, html) => {
            await saveSection.mutateAsync({ sectionId, html });
          }}
          onReviseSelection={reviseSelection}
        />
      ) : (
        <NotGenerated
          name={selected?.name ?? "this agent"}
          sectionCount={selected?.sectionCount ?? 0}
          pending={generate.isPending}
          onGenerate={() => generate.mutate({})}
        />
      )}
    </div>
  );
}

interface Choice {
  key: string;
  name: string;
  icon?: string;
  sectionCount: number;
  document?: AnalysisDocument;
  builtin: boolean;
}

const AnalysisPicker = ({
  choices,
  selectedKey,
  onSelect,
  settingsPath,
}: {
  choices: Choice[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
  settingsPath: string;
}) => {
  const selected = choices.find((choice) => choice.key === selectedKey);
  // `icon` is a lucide component name, not an emoji: rendering it directly puts
  // the word "FileText" beside the analysis name.
  const SelectedIcon = analysisIcon(selected?.icon);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
          <SelectedIcon className="h-3.5 w-3.5 text-muted-foreground" />
          {selected?.name ?? "Choose an agent"}
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        {choices.map((choice) => {
          const ChoiceIcon = analysisIcon(choice.icon);
          return (
          <DropdownMenuItem
            key={choice.key}
            onClick={() => onSelect(choice.key)}
            className={cn(
              "gap-2",
              choice.key === selectedKey && "bg-accent/60"
            )}
          >
            <ChoiceIcon className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="flex-1 truncate">{choice.name}</span>
            <span className="text-[11px] text-muted-foreground">
              {choice.document
                ? "Generated"
                : `${choice.sectionCount} ${choice.sectionCount === 1 ? "section" : "sections"}`}
            </span>
          </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to={settingsPath} className="gap-2 text-xs">
            <Settings2 className="h-3.5 w-3.5" />
            Manage agents
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ExportMenu = ({
  document: analysis,
  companyName,
}: {
  document: AnalysisDocument;
  companyName?: string;
}) => {
  const [busy, setBusy] = useState<"pdf" | "word" | null>(null);

  const run = async (kind: "pdf" | "word") => {
    setBusy(kind);
    try {
      if (kind === "pdf") {
        await exportAnalysisToPdf(analysis, { companyName });
      } else {
        await exportAnalysisToWord(analysis, { companyName });
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't build the download"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 text-xs"
          disabled={busy !== null}
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5" />
          )}
          Download
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => void run("word")} className="gap-2 text-xs">
          <FileText className="h-3.5 w-3.5" />
          Word (.docx)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => void run("pdf")} className="gap-2 text-xs">
          <FileText className="h-3.5 w-3.5" />
          PDF
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const NotGenerated = ({
  name,
  sectionCount,
  pending,
  onGenerate,
}: {
  name: string;
  sectionCount: number;
  pending: boolean;
  onGenerate: () => void;
}) => (
  <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
    <Sparkles className="h-7 w-7 text-muted-foreground/50" />
    <div>
      <p className="text-sm font-medium text-foreground">
        {`${name} hasn't been generated for this deal`}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        {`${sectionCount} ${sectionCount === 1 ? "section" : "sections"} will be written from this company's documents, notes and deal information.`}
      </p>
    </div>
    <Button size="sm" className="gap-1.5 text-xs" disabled={pending} onClick={onGenerate}>
      {pending ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Sparkles className="h-3.5 w-3.5" />
      )}
      Generate {name}
    </Button>
  </div>
);

const NoTemplates = ({ settingsPath }: { settingsPath: string }) => (
  <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
    <Plus className="h-7 w-7 text-muted-foreground/50" />
    <div>
      <p className="text-sm font-medium text-foreground">No agents yet</p>
      <p className="mt-1 text-xs text-muted-foreground">
        An agent is a set of sections you write once — a title and a prompt
        each — and then run on any deal.
      </p>
    </div>
    <Button asChild size="sm" className="gap-1.5 text-xs">
      <Link to={settingsPath}>
        <Settings2 className="h-3.5 w-3.5" />
        Create an agent
      </Link>
    </Button>
  </div>
);

export type { AnalysisTemplate, BuiltinAnalysisTemplate };
